/**
 * Sticky ATC Bar (REQ-011 P3-8)
 *
 * Shows a fixed bottom bar when the main ATC button scrolls out of viewport.
 * Uses Intersection Observer for efficient scroll detection.
 *
 * Only applies to variants: trust-sticky, full-bundle
 */

(function () {
  'use strict';

  // REQ-011: FR P3-8 - only require that the bar exists in the DOM.
  // CSS remains the single source of truth for cohort gating (trust-sticky,
  // full-bundle) via data-variant on <body>. We deliberately do NOT check
  // computed display here: GrowthBook assigns data-variant asynchronously,
  // after this deferred script runs, so a first-paint check always saw the
  // server-rendered 'control' default and bailed - leaving the bar wired to
  // nothing. Visibility is re-checked at show time instead; see showStickyBar().
  var stickyBar = document.querySelector('[data-sticky-atc-bar]');
  if (!stickyBar) {
    return;
  }
  var stickyButton = document.querySelector('[data-sticky-atc-button]');
  var stickyPrice = document.querySelector('[data-sticky-atc-price]');
  var stickyQty = document.querySelector('[data-sticky-atc-qty]');
  var stickyUnit = document.querySelector('[data-sticky-atc-unit]');
  var mainATCButton = document.querySelector('[data-js-product-add-to-cart]');
  var productForm = document.querySelector('[data-js-product-form]');
  var bundlesContainer = document.querySelector('.bundles-container');

  if (!stickyBar || !mainATCButton) {
    return;
  }

  /**
   * Show the sticky bar with animation
   */
  function showStickyBar() {
    // Cohort gate: CSS keeps the bar display:none for control/buttons-only.
    // Checked here rather than at init so GrowthBook has resolved by now.
    if (window.getComputedStyle(stickyBar).display === 'none') {
      return;
    }
    stickyBar.classList.add('is-visible');
    stickyBar.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide the sticky bar with animation
   */
  function hideStickyBar() {
    stickyBar.classList.remove('is-visible');
    stickyBar.setAttribute('aria-hidden', 'true');
  }

  /**
   * Check if cart drawer/sidebar is open
   */
  function isCartOpen() {
    return document.body.classList.contains('sidebar-opened');
  }

  // Trigger: the main ATC button itself
  // Sticky bar appears only when the actual Add to Cart button leaves the viewport
  var triggerElement = mainATCButton;
  var atcIsOffScreen = false;

  /**
   * Handle intersection observer callback
   * Shows sticky bar when trigger element scrolls out of viewport (upward)
   */
  function handleIntersection(entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        atcIsOffScreen = false;
        hideStickyBar();
      } else {
        // Trigger element not visible — show if scrolled past it (not above it)
        var rect = triggerElement.getBoundingClientRect();
        if (rect.bottom < 0) {
          atcIsOffScreen = true;
          if (!isCartOpen()) {
            showStickyBar();
          }
        } else {
          atcIsOffScreen = false;
          hideStickyBar();
        }
      }
    });
  }

  /**
   * Handle sticky button click - trigger main form submission
   * Guards against double-clicks by disabling until cart opens/closes
   */
  var isAddingToCart = false;

  function handleStickyButtonClick() {
    if (isAddingToCart || !mainATCButton || mainATCButton.disabled) {
      return;
    }
    isAddingToCart = true;
    stickyButton.disabled = true;
    mainATCButton.click();

    // Re-enable after cart drawer opens or after a timeout fallback
    var reenableTimeout = setTimeout(function () {
      isAddingToCart = false;
      stickyButton.disabled = false;
    }, 3000);

    // Watch for cart drawer opening as a signal that add-to-cart completed
    var unlockOnCart = new window.MutationObserver(function () {
      if (isCartOpen()) {
        clearTimeout(reenableTimeout);
        unlockOnCart.disconnect();
        // Re-enable after a short delay so user can't spam while drawer animates
        setTimeout(function () {
          isAddingToCart = false;
          stickyButton.disabled = false;
        }, 500);
      }
    });
    unlockOnCart.observe(document.body, { attributes: true });
  }

  /**
   * Get current bundle quantity from selected bundle
   */
  function getCurrentBundleQty() {
    var selectedBundle = document.querySelector('.bundles__bundle-item--selected');
    if (selectedBundle) {
      // Try to get quantity from data attribute or bundle info
      var qtyEl = selectedBundle.querySelector('[data-bundle-qty]');
      if (qtyEl) {
        return qtyEl.getAttribute('data-bundle-qty');
      }
      // Fallback: parse from bundle heading text (e.g., "1 Box", "3 Boxes")
      var headingEl = selectedBundle.querySelector('.bundle-item__heading, .bundle-item__heading--custom');
      if (headingEl) {
        var match = headingEl.textContent.match(/(\d+)/);
        if (match) {
          return match[1];
        }
      }
    }
    return '1';
  }

  /**
   * Update sticky bar quantity display
   */
  function updateStickyQty() {
    if (stickyQty) {
      var qty = getCurrentBundleQty();
      stickyQty.textContent = qty + ' ×';
    }
  }

  /**
   * Update sticky bar price and unit from selected bundle
   */
  function updateStickyPrice() {
    var selectedBundle = document.querySelector('.bundles__bundle-item--selected');
    if (selectedBundle) {
      // Get per-unit price from the selected bundle (e.g., "$40" from "$40/can")
      var bundlePriceEl = selectedBundle.querySelector('.bundle-item__price');
      if (bundlePriceEl && stickyPrice) {
        stickyPrice.textContent = bundlePriceEl.textContent.trim();
      }
      // Get unit text from the selected bundle (e.g., "/can")
      var bundleUnitEl = selectedBundle.querySelector('.bundle-item__unit');
      if (bundleUnitEl && stickyUnit) {
        stickyUnit.textContent = bundleUnitEl.textContent.trim();
      }
    } else if (stickyPrice) {
      // Fallback: use the main price display
      var priceEl = document.querySelector('[data-update-block="price-compact"] .product-price__price');
      if (priceEl) {
        stickyPrice.textContent = priceEl.textContent.trim();
      }
    }
  }

  /**
   * Update sticky bar when variant changes
   */
  function handleVariantChange(event) {
    var newVariant = event.detail;
    if (!newVariant) return;

    // Update price
    if (stickyPrice && newVariant.price) {
      stickyPrice.textContent = Shopify.formatMoney(newVariant.price, window.theme?.moneyFormat || '${{amount}}');
    }

    // Update button state
    if (stickyButton) {
      if (newVariant.available) {
        stickyButton.disabled = false;
        stickyButton.textContent = window.theme?.strings?.addToCart || 'Add to Cart';
      } else {
        stickyButton.disabled = true;
        stickyButton.textContent = window.theme?.strings?.soldOut || 'Sold Out';
      }
    }
  }

  // Set up Intersection Observer on the trigger element (product title/price)
  var observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0
  };

  var observer = new window.IntersectionObserver(handleIntersection, observerOptions);
  observer.observe(triggerElement);

  // Set up click handler for sticky button
  if (stickyButton) {
    stickyButton.addEventListener('click', handleStickyButtonClick);
  }

  // Listen for variant changes
  document.addEventListener('variant:changed', handleVariantChange);

  // Listen for bundle selection changes
  if (bundlesContainer) {
    bundlesContainer.addEventListener('click', function (e) {
      var bundleItem = e.target.closest('.bundles__bundle-item');
      if (bundleItem) {
        // Small delay to let the selection update
        setTimeout(function () {
          updateStickyQty();
          updateStickyPrice();
        }, 100);
      }
    });
  }

  // Also listen for the theme's variant change event (if different)
  if (productForm) {
    productForm.addEventListener('change', function () {
      // Small delay to let the variant update
      setTimeout(function () {
        updateStickyPrice();
        updateStickyQty();
      }, 100);
    });
  }

  // Hide sticky bar when cart opens, re-show when cart closes (if ATC is off-screen)
  var cartObserver = new window.MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.attributeName === 'class') {
        if (isCartOpen()) {
          hideStickyBar();
        } else if (atcIsOffScreen) {
          showStickyBar();
        }
      }
    });
  });

  cartObserver.observe(document.body, { attributes: true });

  // Initial quantity and price/unit update
  updateStickyQty();
  updateStickyPrice();

  // Init marker: proves this script got past its guard and wired up the
  // observer. A silent early return is what made REQ-011 P3-8 fail invisibly,
  // so expose the state rather than leaving it undiagnosable.
  window.__stickyAtcReady = true;
})();
